import type { ProviderApplicationStatusResponse } from "./tinkr-api";

/**
 * Persist a returning provider's onboarding/gate status so the dashboard can
 * hydrate it INSTANTLY on the next load instead of showing the "Checking your
 * provider application and launch gate" skeleton while the (uncached, sometimes
 * cold-start-slow) `/v1/provider/application` fetch resolves. To an already
 * onboarded provider that skeleton reads like being re-reviewed even though they
 * were activated long ago.
 *
 * What is stored — a NARROW, non-sensitive projection (`Snapshot`):
 *  - Only the enum / boolean / locale / timestamp fields the dashboard actually
 *    reads to render the active state. The application's PII (contactEmail,
 *    companyName, businessWebsiteUrl) and admin-review fields (adminReason, etc.)
 *    are deliberately NOT persisted — the provider's own dashboard never renders
 *    them (they are admin-review only), so they must not sit in localStorage
 *    (Codex #944). `readCachedProviderAccess` reconstructs a type-valid response
 *    with those fields blanked; the real values arrive from the on-mount
 *    revalidation a moment later.
 *
 * Safety posture — this is a UI hydration hint ONLY:
 *  - The server re-checks eligibility on every write and every panel fetch is
 *    server-gated, so a stale hint can never grant access; at worst a
 *    just-suspended provider sees their dashboard shell for one revalidation
 *    round-trip (no data leaks — those calls are server-gated).
 *  - We ONLY cache and ONLY hydrate the fully-onboarded `provider_created`
 *    state, so a stale entry can never make a pending/rejected user briefly see
 *    the active dashboard.
 *  - Scoped to the signed-in user id + a 1-day TTL; the app always revalidates
 *    on mount.
 */
const STORAGE_KEY = "tinkr.provider-access";
const TTL_MS = 24 * 60 * 60 * 1000;

type ApplicationT = NonNullable<ProviderApplicationStatusResponse["application"]>;
type ProviderSummaryT = NonNullable<ProviderApplicationStatusResponse["provider"]>;

type Snapshot = {
  userId: string;
  at: number;
  gateStatus: ProviderApplicationStatusResponse["gate"]["status"];
  canStartProviderOnboarding: boolean;
  providerNetworkLive: boolean;
  networkUpdatedAt: string | null;
  entityType: ApplicationT["entityType"] | null;
  applicationStatus: ApplicationT["status"] | null;
  applicationPreferredLocale: string | null;
  applicationCreatedAt: string | null;
  operationalStatus: ProviderSummaryT["operationalStatus"] | null;
  businessVerificationStatus: ProviderSummaryT["businessVerificationStatus"] | null;
  providerSubmittedForReviewAt: string | null;
  /** Admin review decision (enum only). Optional: snapshots written before
   * this field existed hydrate as "unknown" and revalidate as before. */
  businessReviewStatus?: NonNullable<ProviderSummaryT["businessReview"]>["status"] | null;
};

function toSnapshot(userId: string, status: ProviderApplicationStatusResponse): Snapshot {
  return {
    userId,
    at: Date.now(),
    gateStatus: status.gate.status,
    canStartProviderOnboarding: status.gate.canStartProviderOnboarding ?? false,
    providerNetworkLive: status.network?.providerNetworkLive ?? false,
    networkUpdatedAt: status.network?.updatedAt ?? null,
    entityType: status.application?.entityType ?? null,
    applicationStatus: status.application?.status ?? null,
    applicationPreferredLocale: status.application?.preferredLocale ?? null,
    applicationCreatedAt: status.application?.createdAt ?? null,
    operationalStatus: status.provider?.operationalStatus ?? null,
    businessVerificationStatus: status.provider?.businessVerificationStatus ?? null,
    providerSubmittedForReviewAt: status.provider?.submittedForReviewAt ?? null,
    businessReviewStatus: status.provider?.businessReview?.status ?? null
  };
}

function fromSnapshot(snap: Snapshot): ProviderApplicationStatusResponse {
  return {
    ok: true,
    network: { providerNetworkLive: snap.providerNetworkLive, updatedAt: snap.networkUpdatedAt },
    application:
      snap.applicationStatus === null
        ? null
        : {
            id: "",
            userId: "",
            entityType: snap.entityType ?? "registered_business",
            // PII intentionally NOT persisted — blanked here and replaced by the
            // on-mount revalidation. The provider's own dashboard never renders
            // these (admin-review fields), so the blank values are never shown.
            companyName: null,
            contactEmail: "",
            businessWebsiteUrl: null,
            preferredLocale: snap.applicationPreferredLocale ?? "en",
            status: snap.applicationStatus,
            adminReviewedByUserId: null,
            adminReviewedAt: null,
            adminReason: null,
            networkLiveNotificationQueuedAt: null,
            createdAt: snap.applicationCreatedAt ?? "",
            updatedAt: snap.applicationCreatedAt ?? ""
          },
    provider:
      snap.operationalStatus === null
        ? null
        : {
            id: "",
            operationalStatus: snap.operationalStatus,
            businessVerificationStatus: snap.businessVerificationStatus ?? "unverified",
            submittedForReviewAt: snap.providerSubmittedForReviewAt,
            ...(snap.businessReviewStatus ? { businessReview: { status: snap.businessReviewStatus } } : {}),
            lastAdminRejection: null
          },
    gate: { status: snap.gateStatus, canStartProviderOnboarding: snap.canStartProviderOnboarding }
  };
}

export function readCachedProviderAccess(
  userId: string | undefined
): ProviderApplicationStatusResponse | null {
  if (!userId) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as Partial<Snapshot>;
    if (snap.userId !== userId || typeof snap.at !== "number") return null;
    if (Date.now() - snap.at > TTL_MS) return null;
    // Only hydrate the fully-onboarded state; anything else is genuinely mid-flow
    // and must show the real loading/lockout path, not an optimistic dashboard.
    if (snap.gateStatus !== "provider_created") return null;
    return fromSnapshot(snap as Snapshot);
  } catch {
    return null;
  }
}

export function writeCachedProviderAccess(
  userId: string | undefined,
  status: ProviderApplicationStatusResponse
): void {
  if (!userId) return;
  try {
    // Never persist a transient/pending state — only the onboarded one is safe
    // to optimistically re-show. Drop any prior entry otherwise.
    if (status.gate?.status !== "provider_created") {
      clearCachedProviderAccess();
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(toSnapshot(userId, status)));
  } catch {
    // Storage full/blocked: the next load simply falls back to the fetch skeleton.
  }
}

export function clearCachedProviderAccess(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
